import React from 'react';
import { Chip, Grid, Paper, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderData } from '../../../api';
import { WorkshopCircle } from '../../../common/ui/WorkshopCircle';
import { CARD_FILL } from '../areasPalette';
import { BODY_WEIGHT, CARD_PADDING_INLINE, CARD_RADIUS, FONT_SIZE, ICON_SIZE, MUTED_TEXT } from '../agendaStyles';
import { AgendaCardState } from '../utils/occupancyMath';

type SettledOrderLineProps = {
  order: OrderData;
  name: string;
  timeRange: string;
  account: AccountData;
  language: 'he' | 'en';
  formatAmount: (amount: number) => React.ReactNode;
  onToggleExpanded: () => void;
};

/**
 * A settled customer is finished business: one quiet line, so the open work stands out. Tap opens
 * the full card with its actions.
 */
export const SettledOrderLine = ({
  order,
  name,
  timeRange,
  account,
  language,
  formatAmount,
  onToggleExpanded,
}: SettledOrderLineProps) => {
  const { t } = useTranslation();
  return (
    <Paper
      variant="outlined"
      data-order-id={order.id}
      data-agenda-item-id={order.id}
      role="button"
      tabIndex={0}
      aria-expanded={false}
      onClick={onToggleExpanded}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggleExpanded();
        }
      }}
      sx={{
        borderRadius: CARD_RADIUS,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: CARD_FILL[AgendaCardState.DONE],
        cursor: 'pointer',
        paddingInline: CARD_PADDING_INLINE,
        paddingBlock: 1,
      }}>
      <Grid container alignItems="center" columnGap={1.5} rowGap={0.5} wrap="wrap">
        <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.secondary, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {timeRange}
        </Typography>
        <Grid sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <WorkshopCircle account={account} orderPageId={order.order_page_id} withTooltip language={language} />
        </Grid>
        <Typography
          sx={{
            fontSize: FONT_SIZE.secondary,
            fontWeight: 600,
            flex: '1 1 96px',
            minWidth: '96px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}>
          {name || t('calendar.areas_view.unnamed_order')}
        </Typography>
        {order.order_number !== undefined && (
          <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.detail, fontWeight: BODY_WEIGHT, color: MUTED_TEXT }}>
            #{order.order_number}
          </Typography>
        )}
        {order.balance_closed_at && (
          <Chip
            size="small"
            label={t('calendar.areas_view.closed_with_note_chip', {
              amount: formatAmount(Math.max(0, order.balance_due ?? 0)),
            })}
            sx={{
              color: 'mb.clayInk',
              backgroundColor: 'mb.claySoft',
              fontWeight: 600,
              fontSize: 13,
              borderRadius: '4px',
            }}
          />
        )}
        <Typography
          dir="ltr"
          sx={{ fontSize: FONT_SIZE.detail, fontWeight: BODY_WEIGHT, color: MUTED_TEXT, whiteSpace: 'nowrap' }}>
          {order.persons}
        </Typography>
        <CheckIcon sx={{ fontSize: ICON_SIZE, color: 'primary.main', flexShrink: 0 }} />
      </Grid>
      {order.balance_closed_at && (
        <Typography sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, color: MUTED_TEXT, marginTop: 0.5 }}>
          {t('calendar.areas_view.closed_with_note_line', { note: order.balance_closed_note })}
        </Typography>
      )}
    </Paper>
  );
};
